import { useCallback } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import {
  BookingCellId,
  BookingDto,
  getDateCalendar,
  selectedCellsBookingCalendarAtom,
  toBookingCellId,
  useTimezone,
} from "@ryde/shared";

export const useBookingCalendarSelectedIds = () => {
  const { timezone } = useTimezone();

  const [selectedBookingCellsIds, setSelectedBookingCellsIds] = useRecoilState(
    selectedCellsBookingCalendarAtom,
  );

  const distractCellBookingId = useCallback(
    (bookingDto: BookingDto): BookingCellId | undefined => {
      const { bookTime, passengerId } = bookingDto ?? {};

      if (passengerId && bookTime) {
        return toBookingCellId(
          passengerId,
          getDateCalendar(bookTime, timezone),
        );
      }

      return undefined;
    },
    [timezone],
  );

  const addSelectedIdCalendar = useEvent((bookingDtos: BookingDto[]): void => {
    const newCalendarBookingIds: BookingCellId[] = bookingDtos
      .map(distractCellBookingId)
      .filter((id): id is BookingCellId => id !== undefined);

    setSelectedBookingCellsIds(
      bookingCellsIds =>
        new Set([...bookingCellsIds, ...newCalendarBookingIds]),
    );
  });

  const removeSelectedIdsCalendar = useEvent(
    (bookingDtos: BookingDto[]): void => {
      const passengerIdsToRemove = new Set(
        bookingDtos
          .map(distractCellBookingId)
          .filter((id): id is BookingCellId => id !== undefined),
      );

      setSelectedBookingCellsIds(bookingCellsIds => {
        const newSet = new Set(
          [...bookingCellsIds].filter(id => {
            return !passengerIdsToRemove.has(id);
          }),
        );
        return newSet;
      });
    },
  );

  const clearSelectedIdsCalender = useEvent((): void => {
    setSelectedBookingCellsIds(new Set());
  });

  return {
    selectedBookingCellsIds,
    addSelectedIdCalendar,
    removeSelectedIdsCalendar,
    clearSelectedIdsCalender,
  };
};
